<template>
  <FsDialog v-model="show_create_project_popup" title="Create New Project" width="480px">
    <FsForm :model="model" :rules="rules" ref="formRef" horizontal>
      <FsFormItem prop="projectName" label="Project Name">
        <FsInput v-model="model.projectName" new-regular @input="handleProjectNameInput" />
      </FsFormItem>
      <FsFormItem prop="description" label="Description">
        <FsInput v-model="model.description" placeholder="This is a project description." type="textarea" new-regular>
        </FsInput>
      </FsFormItem>
      <FsFormItem class="btn_box">
        <FsButton
          @click.prevent="submit"
          size="mediumSmall"
          type="red"
          :loading="btnLoading"
          style="margin-bottom: 10px"
          >Create</FsButton
        >
        <!-- <span class="cancel_btn" @click.stop="hideDialog">Cancel</span> -->
      </FsFormItem>
    </FsForm>
  </FsDialog>
</template>

<script setup lang="ts">
import { ref, reactive, watch } from 'vue'
import { FsDialog, FsForm, FsFormItem, FsButton, FsInput, FsSelect } from 'fs-design'

import { useHeaderStore } from '@/store/header'
import { useProjectStore } from '@/store/project'

import { storeToRefs } from 'pinia'
import { createProject, createNewDiag, copyDiagram, bindTemplate } from '@/api/modules/project'
import { useRoute, useRouter } from 'vue-router'
import { useProductSelectionStore } from '@/store/productSelection'
import { trackEvent } from '@/utils/analytics'

const productSelectionStore = useProductSelectionStore()
const route = useRoute()
const headerStore = useHeaderStore()
const projectStore = useProjectStore()
const router = useRouter()
const { show_create_project_popup, createProjectPramas } = storeToRefs(headerStore)

const { allProjectNames } = storeToRefs(projectStore)
const btnLoading = ref<boolean>(false)
const model = reactive({
  projectName: 'New Project',
  description: '',
})

const formRef = ref()

const rules = {
  projectName: [
    {
      type: 'string',
      required: true,
      trigger: 'blur',
      message: 'Project Name is required',
    },
  ],
}

// 过滤中文字符
const handleProjectNameInput = (value: string | number) => {
  const strValue = String(value)
  console.log(strValue, 'strValue')
  // 移除所有中文字符（包括中文标点符号）
  const filtered = strValue.replace(/[\u4e00-\u9fa5\u3000-\u303f\uff00-\uffef]/g, '')
  // 使用 nextTick 确保在下一个 tick 更新，避免 v-model 冲突
  if (filtered !== strValue) {
    setTimeout(() => {
      model.projectName = filtered
    }, 0)
  }
}

const getUniqueProjectName = (inputName: string, existingNames: string[]): string => {
  let baseName = inputName.trim()
  let newName = baseName
  let index = 1

  // 构建一个 Set 加速查找
  const nameSet = new Set(existingNames)

  // 尝试直到找到不重复的名字
  while (nameSet.has(newName)) {
    newName = `${baseName}(${index})`
    index++
  }

  return newName
}

const getCopyName = (name: string): string => {
  const copyPattern = /(.*?_copy)(?:\((\d+)\))?$/

  const match = name.match(copyPattern)

  if (match) {
    const base = match[1] // like "xxx_copy"
    const version = match[2] ? parseInt(match[2]) + 1 : 2
    return `${base}(${version})`
  }

  return `${name}_copy`
}
const submit = async () => {
  try {
    await formRef.value.validate()
    btnLoading.value = true
    let data: any = {
      name: model.projectName,
      description: model.description,
    }
    trackEvent({
      eventCategory: 'des_project_create',
      eventAction: 'create',
      eventLabel: 'Submit',
    })
    if (createProjectPramas.value) {
      Object.assign(data, createProjectPramas.value)
    }
    // if (route.name === 'DesignCenter' && route.query.projectId && route.query.share) {
    //   copyDiagram({
    //     projectId: route.query.projectId,
    //     ...data,
    //   })
    //     .then((res: any) => {
    //       btnLoading.value = false
    //       projectStore.getAllProjectList()
    //       projectStore.setCurrentProjectId(res.data.projectId)
    //       projectStore.setCurrentTemplateId(res.data.templateId)
    //       resetForm()
    //       hideDialog()
    //       router.replace({ path: '/design-center' })
    //     })
    //     .catch(err => {
    //       btnLoading.value = false
    //     })
    // } else {
    if (createProjectPramas?.value?.type === 'useTemplate') {
      data.templateId = createProjectPramas?.value?.templateId
      data.tempTypeId = createProjectPramas?.value?.tempTypeId

      if (allProjectNames?.value?.length && allProjectNames?.value?.includes(data.name)) {
        model.projectName = getUniqueProjectName(model.projectName, allProjectNames?.value)
        data.name = model.projectName
      }
      createNewDiag(data)
        .then((res: unknown) => {
          btnLoading.value = false
          projectStore.getAllProjectList()
          if (route.name === 'Project') {
            projectStore.getProjectList()
          }
          projectStore.setCurrentProjectId(res.data.projectId)
          projectStore.setCurrentTemplateId(res.data.templateId)
          resetForm()
          hideDialog()
          productSelectionStore.clearSelectedProductIds()
        })
        .catch(error => {
          btnLoading.value = false
        })
    } else if (createProjectPramas?.value?.type === 'share') {
      copyDiagram({
        projectId: createProjectPramas?.value.projectId,
        ...data,
      })
        .then((res: unknown) => {
          btnLoading.value = false
          projectStore.getAllProjectList()
          projectStore.setCurrentProjectId(res.data.projectId)
          projectStore.setCurrentTemplateId(res.data.templateId)
          resetForm()
          hideDialog()
          router.replace({ path: '/design-center' })
        })
        .catch(err => {
          btnLoading.value = false
        })
    } else if (createProjectPramas?.value?.type === 'mall') {
      // 商城跳转场景：先创建项目，再绑定模板
      createProject(data)
        .then((res: unknown) => {
          const projectId = res.data as number
          const templateId = createProjectPramas?.value?.templateId
          const tempTypeId = createProjectPramas?.value?.tempTypeId

          // 调用 bindTemplate 绑定模板
          return bindTemplate({
            projectId,
            templateId: Number(templateId),
            tempTypeId: tempTypeId ? Number(tempTypeId) : undefined,
          }).then((bindRes: unknown) => {
            btnLoading.value = false
            projectStore.getAllProjectList()
            projectStore.setCurrentProjectId(projectId)
            projectStore.setCurrentTemplateId(Number(templateId))
            resetForm()
            hideDialog()
            productSelectionStore.clearSelectedProductIds()
          })
        })
        .catch(error => {
          btnLoading.value = false
          console.error('商城创建项目失败:', error)
        })
    } else {
      createProject(data)
        .then((res: unknown) => {
          btnLoading.value = false
          projectStore.getAllProjectList()
          if (route.name === 'Project') {
            projectStore.getProjectList()
          }
          // 先显示弹窗，再设置 projectId，确保 watcher 能正确判断弹窗状态
          headerStore.showSelectTemplatePopup()
          projectStore.setCurrentTemplateId(null)
          resetForm()
          hideDialog()
          // 在下一个 tick 设置 projectId，确保弹窗状态已更新
          setTimeout(() => {
            projectStore.setCurrentProjectId(res.data)
          }, 0)
        })
        .catch(error => {
          btnLoading.value = false
        })
    }
    // }
  } catch (e) {
    console.log('the promise', e)
  }
}

const resetForm = () => {
  formRef.value.resetFields()
  model.projectName = 'New Project'
  model.description = ''
}

const hideDialog = () => {
  headerStore.hideCreateProjectPopup()
  resetForm()
}

watch(show_create_project_popup, (newVal, oldVal) => {
  if (newVal) {
    if (createProjectPramas?.value?.type === 'share') {
      model.projectName = getCopyName(createProjectPramas?.value?.projectName)
    } else {
      model.projectName = 'New Project'
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(.fs-form__item .fs-textarea__number) {
  display: none;
}

.btn_box {
  margin-top: 20px;

  :deep(.fs-form__item--content) {
    display: flex;
    flex-direction: row-reverse;

    .fs-button {
      margin-left: 16px;
    }
  }

  .cancel_btn {
    @include textButton();
  }
}
</style>
